import { useEffect, useState } from 'react';
import { z } from 'zod';
import {
  LiliToastDefaultConfig,
  LiliNotificationType,
  LiliNotification,
} from '@lili/lili-frontend-common';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useFormHook, useSetError } from '@Hooks/index';
import { useCountdownTimer } from '@Hooks/useTimer';

import { useNavigationManager } from '@Router/useNavigationManager';

import { OnboardingFormFields } from '@Types/index';
import { useBoundStore } from '@Store/useBoundStore';
import { RoutesMap } from '@Router/router.types';

import { UboOnboardingAnalyticsService } from '../UboAnalytcs.service';
import { useValidatePhoneMfa } from './useValidateUBOPhoneMfa';
import { useStartMfaPhoneVerification } from './startMfaPhoneVerification';

const phoneFormScheme = z.object({
  [OnboardingFormFields.MFA_OTP]: z.string(),
});

type PhoneForm = z.infer<typeof phoneFormScheme>;

export const useValidatePhoneLogic = () => {
  const { t } = useTranslation(['inviteTeamMember']);
  const { resetFormFieldErrors } = useBoundStore((state) => state.errorSlice);
  const { navigateToNextPage } = useNavigationManager();
  const setUboPhoneStateToken = useBoundStore((state) => state.setUboPhoneStateToken);
  const customerId = useBoundStore((state) => state.secondaryCustomerId!);
  const setUboToken = useBoundStore((state) => state.setUboToken);
  const { mutateAsync: validateMfa, isPending: isLoadingValidateMfa } =
    useValidatePhoneMfa(customerId);
  const { mutateAsync: startMfaPhoneVerification, isPending: isLoadingStartMfaPhoneVerification } =
    useStartMfaPhoneVerification({
      customerId,
    });
  const { startTimer, timeLeft } = useCountdownTimer({
    intervalDuration: 30,
    onFinishCountdown: () => {},
  });

  useEffect(() => {
    startTimer();
  }, []);

  const defaultValues: PhoneForm = {
    [OnboardingFormFields.MFA_OTP]: '',
  };

  const formMethods = useFormHook({
    formSchema: phoneFormScheme,
    formDefaultValues: defaultValues,
    schemaTitle: 'validate-phone-form',
    reValidateMode: 'onBlur',
  });

  const handleValidateCode = async (code: string) => {
    const response = await validateMfa({
      answer: code,
    });

    UboOnboardingAnalyticsService.trackUboPhoneSubmit();
    resetFormFieldErrors();

    if (response.skipQuestion && response.token) {
      setUboToken(response.token);
    } else {
      setUboPhoneStateToken(response.stateToken);
    }

    navigateToNextPage(RoutesMap.UBO_ONBOARDING_VALIDATE_PHONE);
  };
  // this is a workaround to force the OTP input to re-render when the code is validated
  // needs to be removed once we have a proper way to clear the OTP input state
  const [otpKey, setOtpKey] = useState(0);

  const clearOtpState = () => {
    resetFormFieldErrors();
    formMethods.clearErrors(OnboardingFormFields.MFA_OTP);
    formMethods.setValue(OnboardingFormFields.MFA_OTP, '');
    setOtpKey((prev) => prev + 1);
  };

  const handleSendMfaCode = async () => {
    await startMfaPhoneVerification();
  };
  const handleSendCode = async () => {
    try {
      await handleSendMfaCode();
      clearOtpState();
      startTimer();
      toast(
        <LiliNotification
          type={LiliNotificationType.SUCCESS}
          text={t('verifyPhone.resendCodeSuccess')}
          id='resend-otp-success'
        />,
        { ...LiliToastDefaultConfig, closeOnClick: true }
      );
    } catch {
      toast(
        <LiliNotification
          type={LiliNotificationType.ERROR}
          text={t('verifyPhone.resendCodeError')}
          id='resend-otp-failure'
        />,
        { ...LiliToastDefaultConfig, closeOnClick: true }
      );
    }
  };
  useSetError({
    formMethods,
  });
  return {
    handleValidateCode,
    handleSendCode,
    isLoadingValidateMfa,
    isLoadingStartMfaPhoneVerification,
    timeLeft,
    formMethods,
    otpKey,
  };
};
